import { AgaveSourceSystem } from 'app/services/agave/agaveConstants';

export const ERPFeatures = {
  accountingDate: 'accountingDate' as const,
};

/**
 * Configuration for individual ERP features.
 * Extensible for future feature-specific properties.
 */
export type FeatureConfig = {
  isAllowed: boolean;
  label?: string; // Optional: custom label for this ERP (e.g., "Transaction Date" vs "Accounting Date")
};

/**
 * Centralized ERP feature configuration matrix.
 * Defines which features are supported by each ERP system.
 *
 * Add new features as new keys (e.g., 'adjustedCost', 'taxCalculation')
 * Add new ERP systems by adding them to this object.
 */
export const ERP_FEATURE_CONFIGS: Record<string, Record<string, FeatureConfig>> = {
  default: {
    accountingDate: {
      isAllowed: false,
    },
  },
  [AgaveSourceSystem.SAGE_300_CRE]: {
    accountingDate: {
      isAllowed: true,
    },
  },
  [AgaveSourceSystem.SAGE_INTACCT]: {
    accountingDate: {
      isAllowed: true,
    },
  },
  [AgaveSourceSystem.VIEWPOINT_VISTA_HOSTED]: {
    accountingDate: {
      isAllowed: true,
    },
  },
};

/**
 * Check if a specific feature is enabled for a given ERP system.
 *
 * @param erp - The ERP system slug (e.g., 'sage-300-cre')
 * @param feature - The feature name (e.g., 'accountingDate')
 * @returns true if the feature is enabled for this ERP, false otherwise
 */
export const isErpFeatureEnabled = (erp: string | null | undefined, feature: string): boolean => {
  // If the ERP is not found, or the feature is not found, return the default value
  if (!erp || !(erp in ERP_FEATURE_CONFIGS) || !(feature in ERP_FEATURE_CONFIGS[erp]))
    return ERP_FEATURE_CONFIGS.default?.[feature]?.isAllowed ?? false;

  // If the ERP is found, and the feature is found, return the value
  return ERP_FEATURE_CONFIGS[erp][feature].isAllowed;
};
